import GifIcon from 'Assets/icons/gif.svg';
import { ReactComponent as KlipyLogo } from 'Assets/icons/klipy-logo.svg';
import { KLIPY_API_KEY } from 'Constants/env';
import React, { useCallback, useId } from 'react';
import {
  ANALYTICS_EVENT_CHAT_GIF_PICKER,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { Input } from '~/lib/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/lib/ui/popover';
import { ScrollArea } from '~/lib/ui/scroll-area';
import { cn } from '~/lib/utils';
import { GifEmptyState } from '~/modules/ui/gif/components/GifEmptyState';
import { useGifPicker } from '~/modules/ui/gif/hooks/useGifPicker';
import { getKlipyGifThumbUrl } from '~/modules/ui/gif/requests';
import type { GifPickerProps, KlipyGif } from '~/modules/ui/gif/types';

export const GifPicker: React.FC<GifPickerProps> = ({ onSelectGif }) => {
  const searchId = useId();
  const {
    open,
    setOpen,
    searchQuery,
    setSearchQuery,
    submitSearch,
    gifs,
    loading,
    loadingMore,
    loadMoreSentinelRef,
    resetGifPickerState,
    showLoadMoreSentinel,
    showNoResultsEmptyState,
    showErrorEmptyState,
  } = useGifPicker(KLIPY_API_KEY);

  const handleSelectGif = useCallback(
    async (gif: KlipyGif) => {
      await Promise.resolve(onSelectGif(gif.id));
      setOpen(false);
      resetGifPickerState();
    },
    [onSelectGif, resetGifPickerState, setOpen]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        pushToGTMDataLayer(String(ANALYTICS_EVENT_CHAT_GIF_PICKER));
      }

      setOpen(nextOpen);

      if (!nextOpen) {
        resetGifPickerState();
      }
    },
    [resetGifPickerState, setOpen]
  );

  if (!KLIPY_API_KEY) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn(
          'absolute bottom-[5px] right-5 z-[1] flex size-[30px] items-center justify-center rounded-full border border-border bg-background'
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'absolute inset-0 flex cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 shadow-none outline-none',
              'hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-0'
            )}
            aria-label="GIF"
            data-testid="chat-button-gif"
          >
            <span
              aria-hidden
              className={cn(
                'pointer-events-none inline-block size-[22px] shrink-0 bg-foreground',
                open && 'bg-primary'
              )}
              style={{
                maskImage: `url(${GifIcon})`,
                WebkitMaskImage: `url(${GifIcon})`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent
        side="top"
        align="end"
        sideOffset={10}
        collisionPadding={12}
        arrowWidth={14}
        arrowHeight={8}
        arrowClassName="drop-shadow-[0_1px_2px_rgba(0,0,0,0.12)]"
        data-testid="chat-gif-picker"
        className={cn(
          'relative flex w-[min(100vw-1.5rem,351px)] max-w-[min(100vw-1.5rem,351px)] flex-col gap-0 rounded-[10px] border border-white bg-white p-0',
          'text-foreground shadow-[0px_4px_12px_rgba(0,0,0,0.12)]'
        )}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <div
          className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[10px]"
          style={{
            height:
              'min(408px, var(--radix-popover-content-available-height, calc(100vh - 3rem)))',
          }}
        >
          <div className="flex w-full shrink-0 items-center gap-4 border-b border-neutral-200 px-4 py-[10px]">
            <label htmlFor={searchId} className="sr-only">
              Search GIFs
            </label>

            <Input
              id={searchId}
              type="text"
              inputMode="search"
              enterKeyHint="search"
              role="searchbox"
              placeholder="Search Klipy"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) {
                  return;
                }

                if (e.key === 'Enter' || e.code === 'NumpadEnter') {
                  e.preventDefault();
                  e.stopPropagation();
                  submitSearch();
                }
              }}
              autoComplete="off"
              className={cn(
                'h-9 w-full min-w-0 rounded-[10px] border border-solid border-border bg-background px-3 py-2 text-sm shadow-none',
                'placeholder:text-muted-foreground',
                'focus:border-primary',
                'focus:outline-none focus:ring-0 focus:ring-offset-0',
                'focus-visible:border-primary',
                'focus-visible:ring-0 focus-visible:ring-offset-0'
              )}
              data-testid="chat-gif-search-input"
            />
          </div>

          {showErrorEmptyState ? (
            <GifEmptyState
              title="We couldn't load GIFs"
              description="Please check your connection or try again."
              data-testid="chat-gif-picker-error-state"
            />
          ) : showNoResultsEmptyState ? (
            <GifEmptyState
              title="No GIFs found."
              description="Try another search to find something fun."
              data-testid="chat-gif-picker-empty-state"
            />
          ) : (
            <ScrollArea className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <div
                className="grid grid-cols-2 gap-2 p-3"
                data-testid="chat-gif-picker-grid"
              >
                {loading && gifs.length === 0 && (
                  <p
                    className="col-span-2 text-center text-sm text-muted-foreground"
                    data-testid="chat-gif-picker-loading"
                  >
                    Loading…
                  </p>
                )}

                {gifs.map((gif) => {
                  const thumb = getKlipyGifThumbUrl(gif);
                  if (!thumb) {
                    return null;
                  }
                  return (
                    <button
                      key={gif.id}
                      type="button"
                      data-testid={`chat-gif-picker-item-${gif.id}`}
                      data-gif-id={gif.id}
                      className={cn(
                        'relative aspect-square w-full cursor-pointer overflow-hidden rounded-md border-0 bg-muted p-0 outline-none',
                        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                      )}
                      aria-label="Insert GIF"
                      onClick={() => {
                        void handleSelectGif(gif);
                      }}
                    >
                      <img
                        src={thumb}
                        alt=""
                        className="size-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  );
                })}

                {showLoadMoreSentinel && (
                  <div
                    ref={loadMoreSentinelRef}
                    className="col-span-2 h-4 shrink-0"
                    aria-hidden
                  />
                )}

                {loadingMore && (
                  <p
                    className="col-span-2 pb-1 text-center text-xs text-muted-foreground"
                    data-testid="chat-gif-picker-loading-more"
                  >
                    Loading…
                  </p>
                )}
              </div>
            </ScrollArea>
          )}

          <div className="shrink-0 border-t border-neutral-200 px-4 py-[18px]">
            <div className="flex w-full min-h-[16px] items-center justify-end gap-2">
              <span className="whitespace-nowrap font-sans text-[12px] font-normal leading-none text-neutral-900">
                Powered by
              </span>
              <KlipyLogo
                aria-hidden
                className="block h-4 w-[45px] shrink-0 [&_svg]:h-full [&_svg]:w-full [&_svg]:object-contain"
              />
              <span className="sr-only">Klipy</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
